import { Button, Input } from '@invent/wl-ui-kit-next/ui';

import { useCan } from '@/domains/auth';
import type { IExportColumn } from '../../api/ranking-process-contracts';
import { internalFieldsApi } from '../../api/internal-fields-api';
import { listsApi } from '../../api/lists-api';
import { useAdminData } from '../../components/admin-page';
import { SCOPE_WORDS } from '../../figures';
import type { IChoice } from '../lists/advanced-filter';
import { FieldPicker, type IFieldSection } from '../lists/field-picker';

/**
 * The column rows a list keeps for its files (RP §9.2, §11.1) — the scoring template's columns on
 * List setup and the finalist file's extra columns on Finalists — and what their codes are chosen
 * from: each row a source, a code, a header and a figure's scope, moved and removed in place.
 *
 * **A code is chosen through the lists' field picker**, its code beside each name: an internal
 * field from the survey's staff-field schema, in the staff form's groups — less the fields a
 * manager owns, whose value survey-api keeps nowhere a template reads — and a question from the
 * survey's list catalogue, its `q.<code>` fields with the prefix cut, less those of no single value
 * (kind `summary`), which survey-api refuses too (a repeat member's `rep.` slot is no question); a
 * figure column's metric from the catalogue's figure series. The catalogue is `responses.browse`'s:
 * without it the catalogue is not asked, and where a read fails the code is typed. A stored code
 * neither offers is named as spelled. Nothing here judges a row: survey-api's 400 does.
 */

/** The list catalogue's prefix on a question's field (survey-api's
 *  `ListFieldRules.AnswerPrefix`). */
const ANSWER_PREFIX = 'q.';
/** The catalogue's kind for a question of no single value — a grid, a repeat, a table — which a
 *  template cannot name (survey-api's `Composite`), so it is not offered. */
const SUMMARY_KIND = 'summary';
/** The catalogue's prefix on a figure series' field, `fig.<metric>.<scope>` (survey-api's
 *  `ListFieldRules.FigureCode`); a dated value or a change has more parts. */
const FIGURE_PREFIX = 'fig.';
const COLUMN_SOURCES = ['internal', 'answer', 'figure'] as const;
/** A source said in words (SR-445: the client read `internal` / `answer` as codes); the stored code
 *  is still what is sent. */
export const SOURCE_WORDS: Record<(typeof COLUMN_SOURCES)[number], string> = {
  internal: 'Staff field',
  answer: 'Survey question',
  figure: 'Figure'
};

/** What a source offers to choose from: `sections` null where its read failed or is not the
 *  caller's — the code is then typed. The offer itself is null while the read is out. */
export interface IOffer {
  sections: Nullable<IFieldSection[]>;
  /** By code, the options a value is chosen from — a select's, an option question's. */
  choices: ReadonlyMap<string, IChoice[]>;
  /** By code, what a value is: a number, a date or a yes / no; a code absent here is typed. */
  kinds: ReadonlyMap<string, ValueKind>;
}

export type ValueKind = 'number' | 'date' | 'bool';

const NO_OFFER: IOffer = { sections: null, choices: new Map(), kinds: new Map() };

/** A staff field's type, or a catalogue field's kind, as the value it holds. */
const VALUE_KINDS: Readonly<Record<string, ValueKind>> = {
  int: 'number',
  decimal: 'number',
  money: 'number',
  percent: 'number',
  number: 'number',
  date: 'date',
  datetime: 'date',
  bool: 'bool'
};

/** Each code's value kind, where its type or kind is one `VALUE_KINDS` names. */
function kindsOf(entries: readonly [string, string][]): ReadonlyMap<string, ValueKind> {
  return new Map(
    entries.flatMap(([code, type]) =>
      VALUE_KINDS[type] ? [[code, VALUE_KINDS[type]] as const] : []
    )
  );
}

/** The survey's offers, read once per editor: the staff fields' (also the fixed condition's
 *  names), and `offerOf` a source's — null while its read is out. */
export function useFieldOffers(surveyCode: string) {
  const canBrowse = useCan()('responses.browse');
  const { data: internalOffer } = useAdminData<IOffer>(async () => {
    const schema = await internalFieldsApi.schema(surveyCode).catch(() => null);
    const fields = schema?.groups.flatMap((group) => group.fields) ?? [];
    return {
      kinds: kindsOf(fields.map(({ code, type }) => [code, type])),
      choices: new Map(
        fields
          .filter((field) => field.options.length > 0)
          .map((field) => [field.code, field.options.map(({ code, label }) => ({ code, label }))])
      ),
      sections:
        schema?.groups
          .map((group, index) => ({
            key: `internal.${index}`,
            title: group.name ?? 'Staff fields',
            fields: group.fields
              .filter((field) => field.managedBy === null)
              .map(({ code, name }) => ({ code, label: name, number: null }))
          }))
          .filter((section) => section.fields.length > 0) ?? null
    };
  });
  const { data: catalogueOffers } = useAdminData<{ answer: IOffer; figure: IOffer }>(
    async (signal) => {
      // Never asked of a route known to refuse the caller.
      const catalogue = canBrowse
        ? await listsApi.fields(surveyCode, signal).catch(() => null)
        : null;
      if (catalogue === null) return { answer: NO_OFFER, figure: NO_OFFER };
      const of = (source: string) =>
        catalogue.groups
          .filter((group) => group.source === source)
          .flatMap((group) => group.fields);
      const questions = of('answer')
        .filter((field) => field.code.startsWith(ANSWER_PREFIX) && field.kind !== SUMMARY_KIND)
        .map((field) => ({ ...field, code: field.code.slice(ANSWER_PREFIX.length) }));
      // A series' metric once, labelled as the catalogue labels it less its "(scope)": the
      // column's scope is chosen beside it.
      const metrics = new Map<string, string>();
      for (const field of of('figure')) {
        const parts = field.code.slice(FIGURE_PREFIX.length).split('.');
        if (!field.code.startsWith(FIGURE_PREFIX) || parts.length !== 2) continue;
        if (!metrics.has(parts[0])) metrics.set(parts[0], field.label.replace(/ \([^)]*\)$/, ''));
      }
      return {
        answer: {
          sections: [{ key: 'answer', title: 'Survey questions', fields: questions }],
          kinds: kindsOf(questions.map(({ code, kind }) => [code, kind])),
          choices: new Map(
            questions
              .filter((field) => (field.options ?? []).length > 0)
              .map((field) => [
                field.code,
                (field.options ?? []).map(({ code, label }) => ({ code, label }))
              ])
          )
        },
        figure:
          metrics.size === 0
            ? NO_OFFER
            : {
                sections: [
                  {
                    key: 'figure',
                    title: 'Figures',
                    fields: [...metrics].map(([code, label]) => ({ code, label, number: null }))
                  }
                ],
                kinds: new Map(),
                choices: new Map()
              }
      };
    }
  );
  const offerOf = (source: string): Nullable<IOffer> =>
    source === 'internal'
      ? internalOffer
      : source === 'answer' || source === 'figure'
        ? (catalogueOffers?.[source] ?? null)
        : NO_OFFER;
  return { internalOffer, offerOf };
}

/** The columns as a save sends them: a scope on a figure's alone. */
export function sentColumns(columns: readonly IExportColumn[]): IExportColumn[] {
  return columns.map((column) =>
    column.source === 'figure'
      ? column
      : { source: column.source, code: column.code, label: column.label }
  );
}

/** The column rows, scrolling inside the card past a screenful, and "Add a column"; `empty` says
 *  that there is none. */
export function ColumnRows({
  columns,
  offerOf,
  empty,
  onChange
}: {
  columns: readonly IExportColumn[];
  offerOf: (source: string) => Nullable<IOffer>;
  empty: string;
  onChange: (columns: IExportColumn[]) => void;
}) {
  const setColumn = (index: number, change: Partial<IExportColumn>) =>
    onChange(columns.map((entry, at) => (at === index ? { ...entry, ...change } : entry)));
  const moveColumn = (index: number, by: number) => {
    const next = [...columns];
    const [moved] = next.splice(index, 1);
    next.splice(index + by, 0, moved);
    onChange(next);
  };
  return (
    <>
      {columns.length === 0 && <p className="text-sm text-muted-foreground">{empty}</p>}
      <div className="grid max-h-[calc(100dvh-16rem)] gap-2 overflow-y-auto pr-1" data-columns>
        {columns.map((column, index) => {
          const n = index + 1;
          return (
            <div key={index} data-column={n} className="flex flex-wrap items-center gap-2">
              <Select
                label={`Column ${n} source`}
                value={column.source}
                options={COLUMN_SOURCES}
                words={SOURCE_WORDS}
                onChange={(source) =>
                  setColumn(index, {
                    source,
                    scope: source === 'figure' ? (column.scope ?? 'individual') : null
                  })
                }
              />
              <CodeInput
                label={`Column ${n} code`}
                value={column.code}
                offer={offerOf(column.source)}
                onChange={(code) => setColumn(index, { code })}
              />
              <Input
                aria-label={`Column ${n} label`}
                className="w-56"
                placeholder="header"
                value={column.label}
                onChange={(event) => setColumn(index, { label: event.target.value })}
              />
              {column.source === 'figure' && (
                <Select
                  label={`Column ${n} scope`}
                  value={column.scope ?? 'individual'}
                  options={[...SCOPE_WORDS.keys()]}
                  words={Object.fromEntries(SCOPE_WORDS)}
                  onChange={(scope) => setColumn(index, { scope })}
                />
              )}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Move column ${n} up`}
                disabled={index === 0}
                onClick={() => moveColumn(index, -1)}
              >
                ↑
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Move column ${n} down`}
                disabled={index === columns.length - 1}
                onClick={() => moveColumn(index, 1)}
              >
                ↓
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Remove column ${n}`}
                onClick={() => onChange(columns.filter((_, at) => at !== index))}
              >
                Remove
              </Button>
            </div>
          );
        })}
      </div>
      <div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onChange([...columns, { source: 'internal', code: '', label: '' }])}
        >
          Add a column
        </Button>
      </div>
    </>
  );
}

export function Select({
  label,
  value,
  options,
  words,
  onChange
}: {
  label: string;
  value: string;
  options: readonly string[];
  words?: Record<string, string>;
  onChange: (value: string) => void;
}) {
  // A stored value this build has no option for is still offered, as spelled.
  const shown = options.includes(value) ? options : [...options, value];
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-9 rounded-md border border-border bg-white px-2 text-sm"
    >
      {shown.map((option) => (
        <option key={option} value={option}>
          {words?.[option] ?? option}
        </option>
      ))}
    </select>
  );
}

/** An entry's code: chosen from the offer's sections where they were read, else typed (a figure's
 *  metric, or a read that failed) — and, while the read is out, a picker held off, so nothing typed
 *  is swapped away under the cursor. */
export function CodeInput({
  label,
  value,
  offer,
  onChange
}: {
  label: string;
  value: string;
  offer: Nullable<IOffer>;
  onChange: (value: string) => void;
}) {
  if (offer?.sections !== null) {
    return (
      <FieldPicker
        label={label}
        sections={offer?.sections ?? []}
        value={value}
        placeholder={offer ? 'Choose…' : 'Reading…'}
        codes
        disabled={offer === null}
        onChoose={onChange}
        className="w-80"
      />
    );
  }
  return (
    <Input
      aria-label={label}
      className="w-56 font-mono text-xs"
      placeholder="code"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
